import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { sessionKey, useSession } from '../lib/session.js';
import { Companion } from '../art/Companion.js';
import { Collectible } from '../art/Collectible.js';
import { Chest } from '../art/Chest.js';
import { buddyLine } from '@qq/shared';
import { sayAloud } from '../lib/speech.js';

interface CollectionItem {
  key: string;
  name: string | null;
  rarity: string;
  found: boolean;
}
interface CollectionSet {
  key: string;
  name: string;
  kind: string;
  owned: number;
  size: number;
  complete: boolean;
  items: CollectionItem[];
}
interface Rewards {
  meter: { progress: number; size: number };
  chestsReady: number;
  starsToday: number;
  streak: { daysThisWeek: number; message: string };
  familyGoal: { target: number; progress: number; complete: boolean; message: string };
}
interface ChestResult {
  opened: boolean;
  collectible?: { key: string; name: string; rarity: string; setName: string };
  guaranteed?: boolean;
  completedSet?: string;
  reason?: string;
}

/** A buddy just chosen says hello: in a bubble, and aloud for 'auto'. */
function BuddyHello({ buddy, speak }: { buddy: { key: string; name: string }; speak: boolean }) {
  const line = `Hi! I'm ${buddy.name}. ${buddyLine('hello', buddy.key)}`;
  useEffect(() => {
    if (speak) sayAloud({ text: line });
  }, [speak, line]);
  return (
    <div className="buddy" data-cheer="true" role="status">
      <Collectible collectibleKey={buddy.key} size={64} title={buddy.name} />
      <p className="buddy-bubble">{line}</p>
    </div>
  );
}

const RARITY_GLYPH: Record<string, string> = {
  common: '🟢',
  uncommon: '🔵',
  rare: '🟣',
  legendary: '🌟',
};

/**
 * The shelf.
 *
 * Unfound pieces show as silhouettes, never as names: the wanting is the whole
 * point of collecting, and spoiling it costs nothing to avoid. Nothing here can
 * be bought, and nothing here can be lost.
 */
export function Collection() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = useSession();
  const child = session.data?.child;
  const [opened, setOpened] = useState<ChestResult | null>(null);

  const rewards = useQuery({
    queryKey: ['rewards'],
    queryFn: () => api<Rewards>('/child/rewards'),
    enabled: Boolean(child),
  });
  const collection = useQuery({
    queryKey: ['collection'],
    queryFn: () => api<{ sets: CollectionSet[] }>('/child/collection'),
    enabled: Boolean(child),
  });

  const chest = useMutation({
    mutationFn: () => api<ChestResult>('/child/chest', { method: 'POST' }),
    onSuccess: (result) => {
      setOpened(result);
      void qc.invalidateQueries({ queryKey: ['rewards'] });
      void qc.invalidateQueries({ queryKey: ['collection'] });
    },
  });

  // A card they have found can come along as their buddy. Choosing one
  // changes nothing about the card - it stays on the shelf, and it is theirs.
  const [greeting, setGreeting] = useState<{ key: string; name: string } | null>(null);
  const buddy = useMutation({
    mutationFn: (buddyKey: string | null) =>
      api<{ buddy: { key: string; name: string } | null }>('/child/me', {
        method: 'PATCH',
        body: { buddyKey },
      }),
    onSuccess: (me) => {
      void qc.invalidateQueries({ queryKey: sessionKey });
      // The buddy says hello straight away: for a child who cannot read
      // "Make Rumble my buddy", hearing Rumble answer is what says it worked.
      if (me.buddy) setGreeting(me.buddy);
    },
  });
  const buddyKey = child?.buddy?.key ?? null;

  if (session.isPending) return <main className="screen">Just a moment…</main>;
  if (!child) return <Navigate to="/" replace />;

  const meter = rewards.data?.meter;
  const ready = rewards.data?.chestsReady ?? 0;

  if (opened?.opened && opened.collectible) {
    const found = opened.collectible;
    return (
      <main className="screen reveal" data-rarity={found.rarity}>
        {/* The rays sit behind everything and turn slowly. Motion is switched
            off wholesale by prefers-reduced-motion; the reveal still happens,
            it just arrives rather than swooping. */}
        <div className="reveal-rays" aria-hidden="true" />
        <div className="reveal-chest">
          <Chest size={150} state="open" />
        </div>
        <div className="reveal-card">
          <Collectible collectibleKey={found.key} size={132} title={found.name} />
          <h1>{found.name}</h1>
          <p className="pill">
            {RARITY_GLYPH[found.rarity]} {found.rarity} · {found.setName}
          </p>
        </div>
        {opened.guaranteed && (
          <p className="gentle-note">
            You&rsquo;d been waiting a while for this one, so we made sure of it.
          </p>
        )}
        {opened.completedSet && (
          <p className="praise-note">
            You finished the whole {opened.completedSet} collection! A new one is waiting.
          </p>
        )}
        <button
          type="button"
          className="btn btn--outline"
          onClick={() => {
            buddy.mutate(found.key);
            setOpened(null);
          }}
        >
          🤝 Make it my buddy
        </button>
        <button type="button" className="btn btn--big" onClick={() => setOpened(null)}>
          Put it on my shelf
        </button>
      </main>
    );
  }

  return (
    <main className="screen collection">
      {/* On its own line, above everything. Wedged into the companion row it
          read as part of the title rather than as the way out. */}
      <button type="button" className="shelf-back" onClick={() => navigate('/home')}>
        &larr; Back home
      </button>

      <div className="activity-top">
        <Companion companionKey={child.companionId} size={72} />
        <h1>{child.nickname}&rsquo;s shelf</h1>
      </div>

      <div className="meter">
        <div
          className="meter-fill"
          style={{ width: `${((meter?.progress ?? 0) / (meter?.size ?? 12)) * 100}%` }}
        />
      </div>
      <p className="muted">
        {ready > 0
          ? 'A chest is ready to open!'
          : `${meter?.progress ?? 0} of ${meter?.size ?? 12} stars towards your next chest`}
      </p>

      {ready > 0 && (
        <button
          type="button"
          className="chest-button"
          disabled={chest.isPending}
          data-opening={chest.isPending}
          onClick={() => chest.mutate()}
        >
          <Chest size={150} state="shut" />
          <span>{chest.isPending ? 'Opening…' : 'Open the chest!'}</span>
        </button>
      )}

      {rewards.data && (
        <>
          <p className="pill">{rewards.data.streak.message}</p>
          <p className="pill">{rewards.data.familyGoal.message}</p>
        </>
      )}

      <p className="muted">🤝 Tap a card you have found to make it your buddy.</p>
      {greeting && (
        <BuddyHello buddy={greeting} speak={child.readAloud === 'auto'} key={greeting.key} />
      )}

      {collection.data?.sets.map((set) => (
        <section key={set.key} className="card card--soft">
          <h2>
            {set.name}{' '}
            <span className="pill">
              {set.owned} of {set.size}
            </span>{' '}
            {set.complete && <span className="pill pill--ok">Complete!</span>}
          </h2>
          <div className="shelf">
            {set.items.map((item) => {
              const card = (
                <>
                  <Collectible
                    collectibleKey={item.key}
                    size={72}
                    title={item.found ? (item.name ?? undefined) : 'Not found yet'}
                    hidden={!item.found}
                  />
                  <span className="shelf-name">{item.name ?? '???'}</span>
                  {item.key === buddyKey && <span className="buddy-badge">🤝 Buddy</span>}
                </>
              );
              // Only a found card can be chosen. A silhouette stays a plain slot.
              return item.found ? (
                <button
                  key={item.key}
                  type="button"
                  className="shelf-slot"
                  data-found="true"
                  data-rarity={item.rarity}
                  data-buddy={item.key === buddyKey}
                  aria-label={`Make ${item.name ?? 'this card'} my buddy`}
                  aria-pressed={item.key === buddyKey}
                  onClick={() => buddy.mutate(item.key)}
                >
                  {card}
                </button>
              ) : (
                <div
                  key={item.key}
                  className="shelf-slot"
                  data-found="false"
                  data-rarity={item.rarity}
                >
                  {card}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <button type="button" className="btn btn--quiet" onClick={() => navigate('/home')}>
        Back home
      </button>
    </main>
  );
}
